/**
 * MOCK/TEMPORARY — commercial licences and public verification.
 *
 * ============================================================================
 * The public verification screen now has the RIGHT SHAPE: it looks a licence up by
 * licence number, unauthenticated, and renders a minimal public projection rather than
 * the owner's whole record. In mock mode that registry is still synthetic, because there
 * is no backend to own it — but the dependency on the signed-in account is gone, which is
 * the architectural step the real backend will complete.
 *
 * The owner-facing reads remain partly local in spirit for the same reason. The app can
 * name the signed-in account and can derive a licence-shaped record for it, but durable
 * provenance and a true registry are still server work. See docs/api-contract.md,
 * "Licenses".
 * ============================================================================
 */
import { USE_MOCK_API } from '../config';
import { DEMO_USER } from '../api/authApi';
import { COMMERCIAL_ADDON } from '../data/products';
import {
  commercialCoverageForOwnedSong,
  generateLicenseNumber,
  verificationUrl,
} from '../lib/license';
import type {
  License,
  LicenseAgreementHistoryItem,
  LicensedSong,
  LicenseActivityEntry,
  Page,
  PublicLicenseVerification,
  Song,
} from '../types/domain';
import { http, type BlobResponse } from './http';
import { assertMock, mockDelay } from './mock';
import { hashString } from '../lib/hash';

/** What the caller must supply, because only the client knows the account. */
export interface LocalLicenseContext {
  ownsCommercial: boolean;
  customerName: string;
  email: string;
  purchaseDate: string;
  version: string;
  downloadCount: number;
}

const MOCK_ISSUED_DATE = 'Jul 30, 2026';
const MOCK_AGREEMENT_TITLE = 'Commercial License Agreement';
const MOCK_RIGHTS_SUMMARY = [
  'Commercial use',
  'Distribution to streaming platforms',
  'Monetized video use',
  'Client work',
];

function buildOwnerLicense(ctx: LocalLicenseContext): License {
  const licenseNumber = generateLicenseNumber(ctx.email);
  return {
    licenseNumber,
    customerName: ctx.customerName,
    email: ctx.email,
    status: 'active',
    issuedDate: ctx.purchaseDate || '—',
    effectiveFrom: ctx.purchaseDate || undefined,
    version: ctx.version,
    agreementTitle: MOCK_AGREEMENT_TITLE,
    product: COMMERCIAL_ADDON.name,
    verifyUrl: verificationUrl(licenseNumber),
    downloadCount: ctx.downloadCount,
    publicLookupEnabled: true,
  };
}

function mockPublicRegistry(): Array<PublicLicenseVerification & { valid: true }> {
  const demoLicenseNumber = generateLicenseNumber(DEMO_USER.email);
  return [
    {
      valid: true,
      licenseNumber: demoLicenseNumber,
      status: 'active',
      licensedTo: DEMO_USER.name,
      issuedDate: MOCK_ISSUED_DATE,
      agreementVersion: '1.0',
      product: COMMERCIAL_ADDON.name,
      rightsSummary: MOCK_RIGHTS_SUMMARY,
      songCoverageSummary: { coveredSongs: 2 },
    },
  ];
}

function coverageFor(
  song: Song,
  ownsCommercial: boolean
): { covered: boolean; coverageReason: string } {
  if (!ownsCommercial) {
    return {
      covered: false,
      coverageReason: 'No commercial licence is active for this account.',
    };
  }
  return commercialCoverageForOwnedSong(song);
}

export const licenseApi = {
  /** Authenticated owner read for the separately persisted RSL-series licence. */
  async getMyResellerLicense(ctx?: {
    ownsReseller: boolean;
    customerName: string;
    email: string;
  }): Promise<License | null> {
    if (!USE_MOCK_API) return http.get<License>('/licenses/me/reseller');
    assertMock('licenseApi.getMyResellerLicense');
    if (!ctx?.ownsReseller) return mockDelay(null);
    const licenseNumber = `RSL-2026-${String(
      hashString(`${ctx.email}Lyrixa-reseller`) % 1_000_000
    ).padStart(6, '0')}`;
    return mockDelay({
      licenseNumber,
      customerName: ctx.customerName,
      email: ctx.email,
      status: 'active',
      issuedDate: MOCK_ISSUED_DATE,
      version: '1.0',
      agreementTitle: 'Lyrixsa AI Reseller Terms',
      product: 'Lyrixsa AI Reseller License',
      verifyUrl: verificationUrl(licenseNumber),
      downloadCount: 0,
      publicLookupEnabled: true,
    });
  },

  /**
   * MOCK: GET /licenses/me
   *
   * Derives the owner's licence-shaped record from local account data in mock mode. A
   * real backend must issue and store the record instead.
   */
  async getMyLicense(ctx: LocalLicenseContext): Promise<License | null> {
    if (!USE_MOCK_API) {
      return http.get<License | null>('/licenses/me');
    }
    assertMock('licenseApi.getMyLicense');
    if (!ctx.ownsCommercial) return mockDelay(null);
    return mockDelay(buildOwnerLicense(ctx));
  },

  /**
   * MOCK: GET /licenses/me/songs
   *
   * Uses the current library as a stand-in for backend provenance. That is enough to
   * exercise the UI shape without pretending the provenance is durable.
   */
  async getLicensedSongs(ctx: LocalLicenseContext, songs: Song[]): Promise<Page<LicensedSong>> {
    if (!USE_MOCK_API) {
      return http.get<Page<LicensedSong>>('/licenses/me/songs');
    }
    assertMock('licenseApi.getLicensedSongs');
    if (!ctx.ownsCommercial) return mockDelay({ items: [], nextCursor: null, total: 0 });
    const items = songs.map((song) => {
      const coverage = coverageFor(song, ctx.ownsCommercial);
      return {
        songId: song.id,
        title: song.title,
        generatedAt: song.date,
        covered: coverage.covered,
        coverageReason: coverage.coverageReason,
        agreementVersionAtGeneration: coverage.covered ? ctx.version : null,
        licenseNumberAtGeneration: coverage.covered ? generateLicenseNumber(ctx.email) : null,
        releaseIds: [],
      };
    });
    return mockDelay({ items, nextCursor: null, total: items.length });
  },

  /** MOCK: GET /licenses/me/activity */
  async getLicenseActivity(): Promise<LicenseActivityEntry[]> {
    if (!USE_MOCK_API) {
      return http.get<LicenseActivityEntry[]>('/licenses/me/activity');
    }
    assertMock('licenseApi.getLicenseActivity');
    return mockDelay([]);
  },

  /** MOCK: GET /licenses/me/agreement-history */
  async getAgreementHistory(ctx: LocalLicenseContext): Promise<LicenseAgreementHistoryItem[]> {
    if (!USE_MOCK_API) {
      return http.get<LicenseAgreementHistoryItem[]>('/licenses/me/agreement-history');
    }
    assertMock('licenseApi.getAgreementHistory');
    if (!ctx.ownsCommercial) return mockDelay([]);
    return mockDelay([
      {
        version: ctx.version,
        title: MOCK_AGREEMENT_TITLE,
        acceptedAt: ctx.purchaseDate || MOCK_ISSUED_DATE,
        publishedAt: 'Jul 1, 2026',
      },
    ]);
  },

  /**
   * MOCK: GET /public/licenses/:licenseNumber  (PUBLIC — no auth)
   *
   * Reads a synthetic registry by licence number rather than the signed-in account.
   */
  async verifyLicensePublic(input: {
    licenseNumber: string;
    title?: string;
    slug?: string;
  }): Promise<PublicLicenseVerification> {
    if (!USE_MOCK_API) {
      const q = input.licenseNumber.trim();
      if (!q) return { valid: false };
      return http.get<PublicLicenseVerification>(`/public/licenses/${encodeURIComponent(q)}`, {
        auth: false,
        query: {
          ...(input.title ? { title: input.title } : {}),
          ...(input.slug ? { slug: input.slug } : {}),
        },
      });
    }
    assertMock('licenseApi.verifyLicensePublic');
    const q = input.licenseNumber.trim().toLowerCase();
    if (!q) return mockDelay({ valid: false });
    const match = mockPublicRegistry().find(
      (record) => (record.licenseNumber ?? '').toLowerCase() === q
    );
    if (!match) return mockDelay({ valid: false });
    const songs = [
      {
        title: 'Summer highway',
        generatedAt: '2026-07-27T00:00:00.000Z',
        coverageReason: "Covered by this account's commercial licence for in-app generated songs.",
        shareSlug: 'abc123',
      },
      {
        title: 'Midnight drive',
        generatedAt: '2026-07-25T00:00:00.000Z',
        coverageReason: "Covered by this account's commercial licence for in-app generated songs.",
      },
    ];
    const normalizedTitle = input.title?.trim().toLowerCase();
    const normalizedSlug = input.slug?.trim().toLowerCase();
    const matchedSong = normalizedSlug
      ? (songs.find((song) => (song.shareSlug ?? '').toLowerCase() === normalizedSlug) ?? null)
      : normalizedTitle
        ? (songs.find((song) => song.title.trim().toLowerCase() === normalizedTitle) ?? null)
        : undefined;
    return mockDelay({
      ...match,
      songs,
      ...(matchedSong !== undefined ? { matchedSong } : {}),
    });
  },

  /** The product name the licence is issued against. */
  licensedProductName(): string {
    return COMMERCIAL_ADDON?.name ?? 'Commercial License & Creator Toolkits';
  },

  /** Requests a PDF built from the authenticated server-owned license record. */
  async downloadCertificatePdf(): Promise<BlobResponse> {
    if (!USE_MOCK_API) {
      return http.requestBlob('/licenses/me/certificate.pdf', {
        method: 'POST',
      });
    }
    assertMock('licenseApi.downloadCertificatePdf');
    throw new Error('Certificate PDF export needs the backend route.');
  },

  /** Requests a PDF render of the authenticated owner's reseller certificate. */
  async downloadResellerCertificatePdf(): Promise<BlobResponse> {
    if (!USE_MOCK_API) {
      return http.requestBlob('/licenses/me/reseller/certificate.pdf', {
        method: 'POST',
      });
    }
    assertMock('licenseApi.downloadResellerCertificatePdf');
    throw new Error('Reseller certificate PDF export needs the backend route.');
  },

  /**
   * MOCK: POST /licenses/me/downloads
   *
   * Records a certificate download for the activity log. The certificate itself
   * is built client-side by lib/license.ts.
   */
  async recordCertificateDownload(): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<void>('/licenses/me/downloads');
      return;
    }
    assertMock('licenseApi.recordCertificateDownload');
    return mockDelay(undefined);
  },
};
